import React from 'react';
import { EmptyStateProps, EmptyStateSize } from '../../../types/DesignSystem/EmptyState';
import { TypographyVariant } from '../../../types/DesignSystem/Typography';
import Typography from '../Typography/Typography';
import { withClassName } from '../cn';

/**
 * EmptyState — the fourth observable state, given a component.
 *
 * `okf/e2e-template.md` requires every module to document loading / empty / error /
 * success, and `Table` already took an `emptyContent` prop, defaulting to the bare
 * string `No data available`. That string is the whole problem with ad-hoc empty
 * states: it says nothing about *why* it is empty or what to do next, and it was
 * hardcoded English inside a shared component.
 *
 * Copy guidance, since this is where it gets decided: `title` names what is absent
 * in the user's words, `description` says what would change it, `action` is the one
 * control that would. "No products match this filter" beats "No data available".
 *
 * `illustration` (§ 64) is the page-level flavour: pass `<NoDataIllustration />` —
 * the house artwork 17 screens hand-rolled as a fixed-colour `<img alt="logo">` —
 * and it renders tokenized (brand navy in light, brand cyan in dark) and decorative
 * (aria-hidden; the title carries the meaning). `icon` remains the small-slot
 * alternative for panel/table emptiness; using both stacks them, so don't.
 */

/*
  The title rides the size (§ 64): `md` is a page-level moment and gets `heading-2`
  (18px, still `ink-secondary` — larger, not louder); `sm` sits inside a table or
  panel where 14px `title-header` is already the loudest thing it should be.
  `art` bounds the illustration slot; the SVG scales itself to the height.
*/
const sizing: Record<EmptyStateSize, { pad: string; gap: string; art: string; title: TypographyVariant }> = {
  sm: { pad: 'tw-py-8 tw-px-4', gap: 'tw-gap-1', art: 'tw-h-24', title: 'title-header' },
  md: { pad: 'tw-py-16 tw-px-6', gap: 'tw-gap-2', art: 'tw-h-40', title: 'heading-2' },
};

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState({
  title,
  description,
  illustration,
  icon,
  action,
  size = 'md',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const { pad, gap, art, title: titleVariant } = sizing[size];
  return (
    <div
      {...rest}
      ref={ref}
      className={withClassName(`tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center ${pad} ${gap}`, className)}
      data-testid={dataTestId}
    >
      {illustration && (
        /* `accent-bold` is the wash the artwork inherits through currentColor —
           the brand navy in light, the brand cyan on the dark ladder. aria-hidden:
           the drawing decorates the sentence, the title IS the sentence. */
        <span
          className={`tw-flex tw-justify-center tw-text-accent-bold tw-mb-2 ${art}`}
          aria-hidden="true"
          data-testid={dataTestId ? `${dataTestId}-illustration` : undefined}
        >
          {illustration}
        </span>
      )}
      {icon && (
        <span className="tw-text-ink-disabled tw-mb-1" aria-hidden="true" data-testid={dataTestId ? `${dataTestId}-icon` : undefined}>
          {icon}
        </span>
      )}
      {/* grey-700, not grey-900: an empty state is a quiet moment, and a heading at
          full ink weight in the middle of a blank surface shouts. */}
      <Typography variant={titleVariant} className="tw-text-ink-secondary" data-testid={dataTestId ? `${dataTestId}-title` : undefined}>
        {title}
      </Typography>
      {/* § 275 — the description takes `body-large` at `md`, which is what § 243 added it
          for verbatim: "lead text: dialog intros, empty-state descriptions, the one
          paragraph a page opens with". `sm` sits inside a table or panel and stays
          `body-primary` — the same discrimination the title already makes above. */}
      {description && (
        <Typography variant={size === 'md' ? 'body-large' : 'body-primary'} className="tw-text-ink-tertiary tw-max-w-sm" data-testid={dataTestId ? `${dataTestId}-description` : undefined}>
          {description}
        </Typography>
      )}
      {action && <div className="tw-mt-3">{action}</div>}
    </div>
  );
});

export default EmptyState;
